/*
 * Rangreza - Infinite scroll for Impulse collection grids
 * v1.0
 *
 * WHY THIS FILE EXISTS AS ITS OWN ASSET:
 * Stock Impulse does not ship a file with this name, so Shopify theme
 * updates can never conflict with it. All behaviour lives here. No edits
 * are made to main-collection.liquid, pagination.liquid or theme.js.
 *
 * HOW IT WORKS:
 * It reads the theme's existing pagination markup to find the "next page"
 * link, fetches that page, and appends its product cards to the current
 * grid. The real <a href> pagination links stay in the DOM (visually
 * hidden) so Google can still crawl page 2, 3, 4... Removing them would
 * strand every product past page 1.
 */
(function () {
  'use strict';

  var S = window.RangrezaInfiniteScrollSettings || {};
  if (S.enabled === false) return;

  // Bail out quietly on browsers without the APIs we need. Standard
  // pagination stays visible in that case because the hiding class is
  // applied by JS, never by CSS alone.
  if (!('IntersectionObserver' in window) || !window.fetch || !window.DOMParser) return;

  var MODE = S.mode === 'button' ? 'button' : 'auto';
  var AUTO_LIMIT = parseInt(S.autoLimit, 10) || 0; // 0 = unlimited
  var PREFETCH = parseInt(S.prefetchDistance, 10) || 800;

  var TEXT = {
    more: S.loadMoreText || 'Load more',
    loading: S.loadingText || 'Loading',
    end: S.endText || '',
    error: S.errorText || 'Could not load more products. Tap to retry.',
    loaded: S.loadedText || 'more products loaded'
  };

  var SEL = {
    container: '#CollectionAjaxContent',
    grid: '.collection-grid__wrapper .grid--uniform',
    pagination: '.pagination',
    next: '.next a'
  };

  var current = null;

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function Loader(container) {
    this.container = container;
    this.grid = container.querySelector(SEL.grid);
    this.pagination = container.querySelector(SEL.pagination);
    this.loading = false;
    this.autoLoads = 0;
    this.destroyed = false;
    this.observer = null;
    this.init();
  }

  Loader.prototype.nextUrl = function () {
    if (!this.pagination) return null;
    var a = this.pagination.querySelector(SEL.next);
    return a && a.getAttribute('href') ? a.href : null;
  };

  Loader.prototype.init = function () {
    // No grid, no pagination, or only one page: nothing to do.
    if (!this.grid || !this.pagination) return;
    if (!this.nextUrl()) return;

    var self = this;

    // Hide the numbered pagination visually but leave it in the DOM.
    this.pagination.classList.add('rz-pagination--hidden');

    this.ui = el('div', 'rz-infinite');

    this.status = el('p', 'rz-infinite__status');
    this.status.setAttribute('aria-live', 'polite');
    this.status.setAttribute('role', 'status');

    this.spinner = el('span', 'rz-infinite__spinner');
    this.spinner.setAttribute('aria-hidden', 'true');

    this.button = el('button', 'btn btn--secondary rz-infinite__button', TEXT.more);
    this.button.type = 'button';
    this.button.addEventListener('click', function () {
      self.load(true);
    });

    this.sentinel = el('div', 'rz-infinite__sentinel');
    this.sentinel.setAttribute('aria-hidden', 'true');

    this.ui.appendChild(this.spinner);
    this.ui.appendChild(this.status);
    this.ui.appendChild(this.button);
    this.ui.appendChild(this.sentinel);

    this.pagination.parentNode.insertBefore(this.ui, this.pagination);

    this.syncButton();
    this.observe();
  };

  Loader.prototype.observe = function () {
    if (MODE !== 'auto') return;
    if (AUTO_LIMIT && this.autoLoads >= AUTO_LIMIT) return;
    if (this.observer) return;

    var self = this;
    this.observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            self.load(false);
            break;
          }
        }
      },
      // Start fetching well before the shopper reaches the bottom. This is
      // what makes it feel instant rather than "scroll, wait, more appears".
      { rootMargin: PREFETCH + 'px 0px', threshold: 0 }
    );
    this.observer.observe(this.sentinel);
  };

  Loader.prototype.unobserve = function () {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  };

  Loader.prototype.load = function (manual) {
    if (this.loading || this.destroyed) return;

    var url = this.nextUrl();
    if (!url) {
      this.finish();
      return;
    }

    var self = this;
    this.loading = true;
    this.button.disabled = true;
    this.button.textContent = TEXT.loading;
    this.ui.classList.add('rz-infinite--loading');

    fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (self.destroyed) return;

        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = doc.querySelector(SEL.grid);
        var newPagination = doc.querySelector(SEL.pagination);
        var added = 0;

        // Move the fetched cards across in one batch so the browser only
        // reflows once. Images do not start downloading until they are
        // adopted into the live document, so nothing is fetched twice.
        if (newGrid) {
          var frag = document.createDocumentFragment();
          while (newGrid.firstElementChild) {
            frag.appendChild(document.adoptNode(newGrid.firstElementChild));
            added++;
          }
          self.grid.appendChild(frag);
        }

        // Swap in the fetched pagination so nextUrl() advances to page+1.
        if (newPagination) {
          newPagination.classList.add('rz-pagination--hidden');
          self.pagination.parentNode.replaceChild(newPagination, self.pagination);
          self.pagination = newPagination;
        } else if (self.pagination) {
          self.pagination.innerHTML = '';
        }

        self.loading = false;
        if (!manual) self.autoLoads++;

        self.announce(added);
        self.afterAppend();

        if (!self.nextUrl()) {
          self.finish();
          return;
        }

        self.syncButton();

        if (MODE === 'auto' && AUTO_LIMIT && self.autoLoads >= AUTO_LIMIT) {
          // Hand control back to the shopper after N automatic loads so the
          // footer stays reachable.
          self.unobserve();
        }
      })
      .catch(function () {
        if (self.destroyed) return;
        self.loading = false;
        self.button.disabled = false;
        self.button.textContent = TEXT.error;
        self.ui.classList.remove('rz-infinite--loading');
        // Stop auto-retrying a failing endpoint; the button still works.
        self.unobserve();
      });
  };

  Loader.prototype.announce = function (count) {
    this.status.textContent = count ? count + ' ' + TEXT.loaded : '';
  };

  Loader.prototype.afterAppend = function () {
    var grid = this.grid;

    // Give the theme and any apps a chance to wire up the new cards
    // (quick shop, colour swatches, currency, review badges).
    try {
      if (window.theme && typeof window.theme.reinitProductGridItem === 'function') {
        window.theme.reinitProductGridItem(grid);
      }
    } catch (e) {}

    try {
      if (window.jdgm && typeof window.jdgm.customizeBadges === 'function') {
        window.jdgm.customizeBadges();
      }
    } catch (e) {}

    try {
      document.dispatchEvent(
        new CustomEvent('rangreza:products:loaded', {
          detail: { grid: grid, total: grid.childElementCount }
        })
      );
    } catch (e) {}
  };

  Loader.prototype.syncButton = function () {
    this.button.hidden = false;
    this.button.disabled = false;
    this.button.textContent = TEXT.more;
    this.ui.classList.remove('rz-infinite--loading');
  };

  Loader.prototype.finish = function () {
    this.unobserve();
    this.button.hidden = true;
    this.button.disabled = true;
    this.ui.classList.remove('rz-infinite--loading');
    this.ui.classList.add('rz-infinite--done');
    if (TEXT.end) this.status.textContent = TEXT.end;
  };

  function teardown() {
    if (current) {
      current.destroyed = true;
      current.unobserve();
      current = null;
    }
  }

  function boot() {
    var container = document.querySelector(SEL.container);
    if (!container) return;
    teardown();
    current = new Loader(container);
  }

  // Impulse replaces the collection grid via AJAX when filters or sort
  // change. Rebuild when that happens. The guard keeps this cheap: our own
  // appends leave .rz-infinite in place, so this is a no-op during scrolling.
  function watch() {
    if (!window.MutationObserver) return;
    var host = document.querySelector('[data-section-type="collection-grid"]') || document.body;
    var timer;
    new MutationObserver(function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        var container = document.querySelector(SEL.container);
        if (!container) return;
        if (!container.querySelector('.rz-infinite')) boot();
      }, 80);
    }).observe(host, { childList: true, subtree: true });
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    boot();
    watch();
  });

  // Theme editor support.
  document.addEventListener('shopify:section:load', function () {
    setTimeout(boot, 50);
  });
})();
